/* global React, SearchSelect, Badge */

const payrollRuleFields = () => [
  ['payout', t('pay.paidOutVerb')],
  ['taxable', t('pay.tax')],
  ['employerContribution', t('pay.aga')],
  ['holidayPay', t('pay.holidayPay')],
  ['pension', t('pay.otp')],
];

const payrollPayTypeValue = (entry = {}) => entry.payType || entry.id || '';

const payrollPayTypeRules = (entry = {}) => {
  if (!payrollPayTypeValue(entry)) {
    return { payout: false, taxable: false, employerContribution: false, holidayPay: false, pension: false };
  }
  return {
    payout: entry.kind !== 'Trekk' && entry.kind !== 'Naturalytelse' && payrollPayTypeValue(entry) !== 'pension-premium',
    taxable: entry.tax !== false,
    employerContribution: entry.aga !== false,
    holidayPay: entry.holiday !== false,
    pension: entry.pension !== false,
  };
};

const payrollPayTypeRulesEqual = (left = {}, right = {}) =>
  payrollRuleFields().every(([key]) => Boolean(left[key]) === Boolean(right[key]));

const payrollPayTypeRuleDescription = (entry = {}) => {
  const rules = payrollPayTypeRules(entry);
  const enabled = payrollRuleFields().filter(([key]) => rules[key]).map(([, label]) => label);
  return enabled.length ? enabled.join(' · ') : t('pay.noCalculationBasis');
};

const payrollPayTypeGroup = (entry = {}) => {
  if (entry.featured) return t('pay.payTypeGroup.common');
  if (!entry.systemType) return t('pay.payTypeGroup.custom');
  if (entry.kind === 'Naturalytelse') return t('pay.payTypeGroup.benefitInKind');
  if (entry.kind === 'Trekk') return t('pay.payTypeGroup.deduction');
  if (entry.kind === 'Trekkfri godtgjørelse') return t('pay.payTypeGroup.taxFreeAllowance');
  return t('pay.payTypeGroup.other');
};

const payrollPayTypeOptions = (types = [], { surface = 'manualEntry', value = '' } = {}) => {
  const groupOrder = [
    t('pay.payTypeGroup.common'),
    t('pay.payTypeGroup.custom'),
    t('pay.payTypeGroup.other'),
    t('pay.payTypeGroup.benefitInKind'),
    t('pay.payTypeGroup.deduction'),
    t('pay.payTypeGroup.taxFreeAllowance'),
  ];
  return types
    .filter((entry) => (entry.active !== false && entry[surface] !== false) || payrollPayTypeValue(entry) === value)
    .map((entry) => ({
      value: payrollPayTypeValue(entry),
      label: `${entry.name || payrollPayTypeValue(entry)}${entry.active === false ? ` (${t('pay.inactive')})` : ''}`,
      description: payrollPayTypeRuleDescription(entry),
      group: payrollPayTypeGroup(entry),
      featured: entry.featured === true,
      definition: entry,
    }))
    .sort((left, right) => {
      const groupDiff = groupOrder.indexOf(left.group) - groupOrder.indexOf(right.group);
      return groupDiff || left.label.localeCompare(right.label, 'nb', { sensitivity: 'base' });
    });
};

function PayrollPayTypeSelect({ types, value, onChange, surface = 'manualEntry' }) {
  return <SearchSelect
    value={value}
    onChange={onChange}
    options={payrollPayTypeOptions(types, { surface, value })}
    placeholder={t('pay.selectSalaryType')}
    searchPlaceholder={t('pay.searchSalaryTypeOrRule')}
    expandLabel={t('pay.showMoreSalaryTypes')}
    max={420}
  />;
}

function PayrollPayTypeRuleSummary({ rules = {} }) {
  return <div className="pay-type-rule-summary" aria-label={t('pay.calculationRules')}>
    {payrollRuleFields().map(([key, label]) => <Badge key={key} variant={rules[key] ? 'success' : 'muted'}>{label}: {rules[key] ? t('common.yes') : t('common.no')}</Badge>)}
  </div>;
}

Object.assign(window, {
  payrollRuleFields,
  payrollPayTypeValue,
  payrollPayTypeRules,
  payrollPayTypeRulesEqual,
  payrollPayTypeOptions,
  PayrollPayTypeSelect,
  PayrollPayTypeRuleSummary,
});
